import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { message } from "antd";
import Load from "../Load/Load";
import {
  createBanner,
  updateBanner,
  uploadImage,
} from "../../Web Apis/webApis";

const AddBanner = ({
  showModal,
  setShowModal,
  action,
  setAction,
  selectedType,
  selectedBanner,
  isEdit,
}) => {
  const [open, setOpen] = useState(showModal);
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState("");
  const [uploadedImageUrl, setUploadedImageUrl] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  console.log("Selected selectedFile for edit:", selectedFile);

  const closeModal = () => {
    setOpen(false);
    setShowModal(false);
    setImagePreview("");
    setUploadedImageUrl("");
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/gif",
        "image/webp",
      ];
      if (!allowedTypes.includes(file.type)) {
        message.error(
          "Please select a valid image file (JPEG, PNG, GIF, WebP)",
        );
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        message.error("Image size should not exceed 5MB");
        return;
      }

      try {
        setLoading(true);
        setSelectedFile(file);
        formik.setFieldValue("imageFile", file);

        // Upload image to server
        const uploadResponse = await uploadImage(file);
        const data = uploadResponse.data?.data || uploadResponse.data;
        const imageUrl =
          data?.imageUrl ||
          data?.url ||
          data?.image ||
          (typeof data === "string" ? data : null);
        if (imageUrl) {
          setUploadedImageUrl(imageUrl);
          formik.setFieldValue("imageUrl", imageUrl);
          message.success("Image uploaded successfully");
        } else {
          throw new Error("Upload response did not contain image URL");
        }

        // Create preview URL for display
        const reader = new FileReader();
        reader.onload = (e) => {
          setImagePreview(e.target.result);
        };
        reader.readAsDataURL(file);
      } catch (error) {
        console.error("Error uploading image:", error);
        message.error(
          error?.response?.data?.message || "Failed to upload image",
        );
        // Reset the file input and state
        setSelectedFile(null);
        event.target.value = null;
        setUploadedImageUrl("");
        formik.setFieldValue("imageUrl", "");
        formik.setFieldValue("imageFile", null);
      } finally {
        setLoading(false);
      }
    }
  };

  const removeImage = () => {
    setImagePreview("");
    setUploadedImageUrl("");
    setSelectedFile(null);
    formik.setFieldValue("imageFile", null);
    formik.setFieldValue("imageUrl", "");
  };

  // Pre-fill image when editing
  useEffect(() => {
    if (isEdit && selectedBanner?.banner_image) {
      setUploadedImageUrl(selectedBanner.banner_image);
      setImagePreview(selectedBanner.banner_image);
    }
  }, [isEdit, selectedBanner]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      title: selectedBanner?.title ?? "",
      description: selectedBanner?.description ?? "",
      imageUrl: selectedBanner?.banner_image ?? "",
      imageFile: null,
      type: selectedBanner?.type ?? selectedType ?? "promotional",
      url: selectedBanner?.url ?? "",
      isActive: selectedBanner?.isActive ?? true,
      sortOrder: selectedBanner?.sortOrder ?? 0,
      // buttonText: selectedBanner?.buttonText ?? "",
      // altText: selectedBanner?.altText ?? ""
    },
    validationSchema: Yup.object({
      title: Yup.string()
        .required("Title is mandatory")
        .max(100, "Title can not be more than 100 characters")
        .min(3, "Title should have at least 3 characters"),
      type: Yup.string()
        .required("Type is mandatory")
        .oneOf(
          ["promotional", "informational", "announcement", "featured"],
          "Invalid banner type",
        ),
    }),
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        // Validate that an image exists: uploaded URL, form value, existing (edit), or file selected (add)
        const imageUrlToUse =
          uploadedImageUrl || values.imageUrl || selectedBanner?.banner_image;
        const hasImageForAdd = !isEdit && selectedFile;
        if (!imageUrlToUse && !hasImageForAdd) {
          message.error("Please upload an image for the banner");
          setSubmitting(false);
          return;
        }

        setLoading(true);

        if (isEdit && selectedBanner?._id) {
          // Check if we have a newly selected file for update
          if (selectedFile) {
            // Update with new file (FormData)
            const formData = new FormData();
            formData.append("banner_image", selectedFile);
            formData.append("title", values.title);
            formData.append("description", values.description);
            formData.append("type", values.type);
            formData.append("url", values.url);
            formData.append("isActive", values.isActive);
            formData.append("sortOrder", parseInt(values.sortOrder));
            // formData.append('buttonText', values.buttonText)
            // formData.append('altText', values.altText)

            console.log("Updating banner with new file via FormData");
            const response = await updateBanner(selectedBanner._id, formData);
            if (response?.data?.success) {
              message.success(
                response?.data?.message || "Banner updated successfully",
              );
              setOpen(false);
              setShowModal(false);
              setAction(!action);
              setImagePreview("");
              setUploadedImageUrl("");
            }
          } else {
            // Update existing banner (JSON) - no new file selected
            const payload = {
              title: values.title,
              description: values.description,
              type: values.type,
              url: values.url,
              isActive: values.isActive,
              sortOrder: parseInt(values.sortOrder) || 0,
              // buttonText: values.buttonText,
              // altText: values.altText,
              banner_image: imageUrlToUse || selectedBanner?.banner_image,
            };
            console.log("Updating banner with payload:", payload.banner_image);
            const response = await updateBanner(selectedBanner._id, payload);
            if (response?.data?.success) {
              message.success(
                response?.data?.message || "Banner updated successfully",
              );
              setOpen(false);
              setShowModal(false);
              setAction(!action);
              setImagePreview("");
              setUploadedImageUrl("");
            }
          }
        } else {
          // Create new banner (FormData) - file required (we already validated hasImageForAdd or imageUrlToUse above)
          if (!selectedFile) {
            message.error("Please choose an image file for the banner");
            setSubmitting(false);
            setLoading(false);
            return;
          }
          const formData = new FormData();

          formData.append("banner_image", selectedFile);
          formData.append("title", values.title);
          formData.append("description", values.description);
          formData.append("type", values.type);
          formData.append("url", values.url);
          formData.append("isActive", values.isActive);
          formData.append("displayOrder", parseInt(values.sortOrder));
          // formData.append('buttonText', values.buttonText)
          // formData.append('altText', values.altText)
          const response = await createBanner(formData);
          if (response?.data?.success) {
            message.success(
              response?.data?.message || "Banner created successfully",
            );
            resetForm();
            setOpen(false);
            setShowModal(false);
            setAction(!action);
            setImagePreview("");
            setUploadedImageUrl("");
          }
        }
      } catch (error) {
        console.log(
          isEdit
            ? "Error while updating banner"
            : "Error while creating banner",
          error.message,
        );
        message.error(
          error?.response?.data?.message ||
            (isEdit
              ? "Error while updating banner"
              : "Error while creating banner"),
        );
      } finally {
        setSubmitting(false);
        setLoading(false);
      }
    },
  });

  const isSubmitting = formik.isSubmitting || loading;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg shadow-lg w-[600px] max-h-[90vh] overflow-y-auto scrollbar-hidden">
        <h3 className="text-xl font-bold mb-1 text-center bg-gradient-to-br from-teal-950 to-teal-500 py-2 text-white">
          {isEdit ? "Edit Banner" : "Add Banner"}
        </h3>
        <form onSubmit={formik.handleSubmit} className="p-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="w-full">
              <label className="block mb-1 mt-2">Title *</label>
              <input
                type="text"
                name="title"
                className="border border-gray-800 p-2 w-full"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                value={formik.values.title}
                placeholder="Enter banner title"
              />
              {formik.touched.title && formik.errors.title && (
                <p className="text-red-600 text-sm">{formik.errors.title}</p>
              )}
            </div>

            <div className="w-full">
              <label className="block mb-1 mt-2">Type *</label>
              <select
                name="type"
                className="border border-gray-800 p-2 w-full"
                onChange={formik.handleChange}
                value={formik.values.type}
              >
                <option value="promotional">Promotional</option>
                <option value="informational">Informational</option>
                <option value="announcement">Announcement</option>
                <option value="featured">Featured</option>
              </select>
              {formik.touched.type && formik.errors.type && (
                <p className="text-red-600 text-sm">{formik.errors.type}</p>
              )}
            </div>
          </div>

          <div className="w-full">
            <label className="block mb-1 mt-2">Banner Image *</label>
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  id="imageUpload"
                />
                <label
                  htmlFor="imageUpload"
                  className="bg-blue-500 text-white px-4 py-2 rounded-md cursor-pointer hover:bg-blue-600 transition duration-200"
                >
                  Choose Image
                </label>
                {uploadedImageUrl && (
                  <button
                    type="button"
                    onClick={removeImage}
                    className="bg-red-500 text-white px-3 py-2 rounded-md hover:bg-red-600 transition duration-200"
                    title="Remove image"
                  >
                    Remove Image
                  </button>
                )}
              </div>

              {/* Image Preview */}
              {imagePreview && (
                <div>
                  <label className="block mb-2 text-sm font-medium">
                    Image Preview:
                  </label>
                  <div className="relative inline-block">
                    <img
                      src={imagePreview}
                      alt="Banner preview"
                      className="max-w-full max-h-48 object-contain border border-gray-300 rounded-md"
                      onError={(e) => {
                        e.target.style.display = "none";
                        e.target.nextSibling.style.display = "block";
                      }}
                      onLoad={(e) => {
                        e.target.style.display = "block";
                        e.target.nextSibling.style.display = "none";
                      }}
                    />
                    <div className="text-red-500 text-sm mt-1 hidden">
                      Failed to load image.
                    </div>
                  </div>
                </div>
              )}

              <div className="text-sm text-gray-600">
                Supported formats: JPEG, PNG, GIF, WebP. Max size: 5MB
              </div>
            </div>
          </div>

          <div className="w-full">
            <label className="block mb-1 mt-2">Description</label>
            <textarea
              name="description"
              className="border border-gray-800 p-2 w-full h-20"
              onChange={formik.handleChange}
              value={formik.values.description}
              placeholder="Enter banner description"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="w-full">
              <label className="block mb-1 mt-2">Link URL</label>
              <input
                type="url"
                name="url"
                className="border border-gray-800 p-2 w-full"
                onChange={formik.handleChange}
                value={formik.values.url}
                placeholder="https://example.com"
              />
            </div>

            <div className="w-full">
              <label className="block mb-1 mt-2">Sort Order</label>
              <input
                type="number"
                name="sortOrder"
                className="border border-gray-800 p-2 w-full"
                onChange={formik.handleChange}
                value={formik.values.sortOrder}
                min="0"
              />
            </div>
          </div>

          {/* <div className="grid grid-cols-2 gap-4">
                        <div className="w-full">
                            <label className="block mb-1 mt-2">Button Text</label>
                            <input
                                type="text"
                                name="buttonText"
                                className="border border-gray-800 p-2 w-full"
                                onChange={formik.handleChange}
                                value={formik.values.buttonText}
                                placeholder='e.g., Learn More'
                            />
                        </div>

                        <div className="w-full">
                            <label className="block mb-1 mt-2">Alt Text</label>
                            <input
                                type="text"
                                name="altText"
                                className="border border-gray-800 p-2 w-full"
                                onChange={formik.handleChange}
                                value={formik.values.altText}
                                placeholder='Image alt text'
                            />
                        </div>
                    </div> */}

          {/* Checkbox */}
          <div className="flex gap-6 mt-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                name="isActive"
                checked={formik.values.isActive}
                onChange={formik.handleChange}
                className="mr-2"
              />
              Active
            </label>
          </div>

          <div className="flex justify-center gap-6 mt-6">
            <button
              onClick={closeModal}
              className="bg-gray-300 text-black w-52 py-2 px-4 rounded-md hover:bg-gray-400 transition duration-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="py-2 px-4 rounded-md w-52 transition duration-200 mr-2 bg-blue-500 text-white hover:bg-blue-600"
            >
              {isSubmitting
                ? loading
                  ? "Uploading..."
                  : isEdit
                    ? "Updating..."
                    : "Creating..."
                : isEdit
                  ? "Update Banner"
                  : "Create Banner"}
            </button>
          </div>
        </form>
        {loading && <Load />}
      </div>
    </div>
  );
};

export default AddBanner;
